<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import {
  MEDIA_FORMATS,
  BUTTON_TYPES,
  replaceTemplateVariablesByExamples,
} from 'dashboard/helper/templateHelper';

const props = defineProps({
  templateData: {
    type: Object,
    required: true,
  },
  parameterType: {
    type: String,
    required: true,
  },
});

const { t } = useI18n();

const isDarkPreview = ref(false);

const theme = computed(() =>
  isDarkPreview.value
    ? {
        wallpaper: '#0B141A',
        bubble: '#005C4B',
        text: '#E9EDEF',
        headerText: '#E9EDEF',
        footer: '#8696A0',
        time: '#8696A0',
        buttonText: '#53BDEB',
        buttonBorder: '#1D3E35',
        mediaBg: '#1A2C36',
        mediaText: '#8696A0',
        emptyText: '#8696A0',
        emptyHeading: '#E9EDEF',
        codeBg: '#1A2C36',
        tailColor: '#005C4B',
        checkColor: '#53BDEB',
      }
    : {
        wallpaper: '#E5DDD5',
        bubble: '#FFFFFF',
        text: '#1F2C34',
        headerText: '#0B141A',
        footer: '#8294A0',
        time: '#8294A0',
        buttonText: '#0591D9',
        buttonBorder: '#E7EDF2',
        mediaBg: '#F5F6F6',
        mediaText: '#8E8E8E',
        emptyText: '#667781',
        emptyHeading: '#1F2C34',
        codeBg: '#f0f0f0',
        tailColor: '#FFFFFF',
        checkColor: '#53BDEB',
      }
);

const buttonIconMap = {
  [BUTTON_TYPES.URL]: 'i-lucide-external-link',
  [BUTTON_TYPES.PHONE_NUMBER]: 'i-lucide-phone',
  [BUTTON_TYPES.COPY_CODE]: 'i-lucide-copy',
  [BUTTON_TYPES.QUICK_REPLY]: 'i-lucide-reply',
  [BUTTON_TYPES.FLOW]: 'i-lucide-git-branch',
  QUICK_REPLY_OVERFLOW: 'i-lucide-list',
};

const previewTime = new Date().toLocaleTimeString([], {
  hour: '2-digit',
  minute: '2-digit',
});

const headerData = computed(() => props.templateData.header || {});
const bodyData = computed(() => props.templateData.body || {});
const footerData = computed(() => props.templateData.footer || {});
const buttonsData = computed(() => props.templateData.buttons || []);

const headerText = computed(() => {
  if (!headerData.value.enabled || headerData.value.format !== 'TEXT') {
    return '';
  }
  const examples =
    props.parameterType === 'named'
      ? headerData.value.example?.header_text_named_params
      : headerData.value.example?.header_text;

  return replaceTemplateVariablesByExamples({
    parameterType: props.parameterType,
    examples: examples || [],
    templateText: headerData.value.text,
  });
});

const headerMediaPreview = computed(() => {
  if (!headerData.value.enabled) return null;
  const format = headerData.value.format;
  if (!MEDIA_FORMATS.includes(format)) return null;
  return {
    format,
    url: headerData.value.media?.fileUrl || '',
    fileName: headerData.value.media?.fileName || '',
  };
});

const bodyText = computed(() => {
  if (!bodyData.value?.text) return '';
  const examples =
    props.parameterType === 'named'
      ? bodyData.value.example?.body_text_named_params
      : bodyData.value.example?.body_text;

  return replaceTemplateVariablesByExamples({
    parameterType: props.parameterType,
    examples: examples || [],
    templateText: bodyData.value.text,
  });
});

const footerText = computed(() => {
  if (!footerData.value.enabled) return '';
  return footerData.value.text || '';
});

const buttonPreviewItems = computed(() => {
  if (!buttonsData.value?.length) return [];

  const mappedButtons = buttonsData.value.map(button => {
    let text = button.text;
    if (!text && button.type === BUTTON_TYPES.COPY_CODE) {
      text = t('MESSAGE_TEMPLATES.BUILDER.BUTTONS.DROPDOWN.COPY_CODE');
    }
    return {
      ...button,
      text,
      icon: buttonIconMap[button.type] || null,
    };
  });

  if (mappedButtons.length > 3) {
    const visibleButtons = mappedButtons.slice(0, 2);
    const overflowButton = {
      type: 'QUICK_REPLY_OVERFLOW',
      text: t('MESSAGE_TEMPLATES.BUILDER.BUTTONS.SEE_ALL_OPTIONS'),
      icon: buttonIconMap.QUICK_REPLY_OVERFLOW,
    };
    return [...visibleButtons, overflowButton];
  }

  return mappedButtons;
});

const hasPreviewContent = computed(() => {
  return (
    headerText.value ||
    headerMediaPreview.value ||
    bodyText.value ||
    footerText.value ||
    buttonPreviewItems.value.length > 0
  );
});

const mediaPlaceholderLabel = computed(() => {
  if (!headerMediaPreview.value) return '';
  const format = headerMediaPreview.value.format;
  const placeholderKey = {
    IMAGE: t('MESSAGE_TEMPLATES.BUILDER.PREVIEW.IMAGE_HEADER'),
    VIDEO: t('MESSAGE_TEMPLATES.BUILDER.PREVIEW.VIDEO_HEADER'),
    DOCUMENT: t('MESSAGE_TEMPLATES.BUILDER.PREVIEW.DOCUMENT_HEADER'),
  }[format];
  return placeholderKey || format;
});

// WhatsApp rich text formatting: *bold*, _italic_, ~strikethrough~, ```monospace```
function formatWhatsAppText(text) {
  if (!text) return '';
  // Escape HTML first
  let html = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  // Order matters: monospace first (triple backtick), then single markers
  const codeBg = theme.value.codeBg;
  html = html.replace(
    /```([\s\S]*?)```/g,
    `<code style="padding: 1px 4px; border-radius: 3px; background: ${codeBg}; font-size: 13px; font-family: monospace;">$1</code>`
  );
  html = html.replace(/\*([^\s*](?:[^*]*[^\s*])?)\*/g, '<b>$1</b>');
  html = html.replace(/_([^\s_](?:[^_]*[^\s_])?)_/g, '<i>$1</i>');
  html = html.replace(/~([^\s~](?:[^~]*[^\s~])?)~/g, '<s>$1</s>');
  // Preserve newlines
  html = html.replace(/\n/g, '<br>');
  return html;
}

const bodyHtml = computed(() => formatWhatsAppText(bodyText.value));
const headerHtml = computed(() => formatWhatsAppText(headerText.value));
</script>

<template>
  <div
    class="bg-n-solid-2 border border-n-weak rounded-lg p-4 h-full flex flex-col"
  >
    <div class="flex items-center justify-between mb-4">
      <div class="flex items-center gap-2">
        <Icon icon="i-lucide-panel-right" class="size-5 text-n-slate-11" />
        <h4 class="font-medium text-n-slate-12">
          {{ t('MESSAGE_TEMPLATES.BUILDER.PREVIEW.TITLE') }}
        </h4>
      </div>
      <!-- Light / Dark toggle -->
      <div
        class="flex items-center gap-1 rounded-lg border border-n-weak p-0.5"
      >
        <button
          class="flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium transition-colors"
          :class="
            !isDarkPreview
              ? 'bg-n-alpha-2 text-n-slate-12'
              : 'text-n-slate-9 hover:text-n-slate-11'
          "
          @click="isDarkPreview = false"
        >
          <span class="i-lucide-sun size-3.5" />
          {{ t('MESSAGE_TEMPLATES.BUILDER.PREVIEW.LIGHT_MODE') }}
        </button>
        <button
          class="flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium transition-colors"
          :class="
            isDarkPreview
              ? 'bg-n-alpha-2 text-n-slate-12'
              : 'text-n-slate-9 hover:text-n-slate-11'
          "
          @click="isDarkPreview = true"
        >
          <span class="i-lucide-moon size-3.5" />
          {{ t('MESSAGE_TEMPLATES.BUILDER.PREVIEW.DARK_MODE') }}
        </button>
      </div>
    </div>

    <div
      class="flex-1 border border-n-weak/60 rounded-lg flex items-center justify-center px-4 py-8 transition-colors duration-200"
      :style="{ background: theme.wallpaper }"
    >
      <div v-if="hasPreviewContent" class="relative w-full max-w-sm">
        <!-- Bubble tail -->
        <svg
          class="absolute -left-2 top-0"
          width="12"
          height="20"
          viewBox="0 0 12 20"
        >
          <path
            d="M12 0 C12 0 3 0 1 8 C0 11 0 20 0 20 C0 20 4 12 12 8 Z"
            :fill="theme.bubble"
          />
        </svg>

        <div
          class="rounded-lg shadow-sm overflow-hidden"
          :style="{
            background: theme.bubble,
            border: isDarkPreview ? 'none' : '1px solid #E0E0E0',
          }"
        >
          <div class="relative p-0.5 space-y-[10px]">
            <!-- Media header preview -->
            <div v-if="headerMediaPreview">
              <div
                class="rounded-lg overflow-hidden"
                :style="{
                  border: isDarkPreview ? 'none' : '1px solid #E6E6E6',
                }"
              >
                <img
                  v-if="
                    headerMediaPreview.format === 'IMAGE' &&
                    headerMediaPreview.url
                  "
                  :src="headerMediaPreview.url"
                  alt=""
                  class="w-full max-h-52 object-cover"
                />
                <div
                  v-else
                  class="flex flex-col items-center justify-center gap-2 py-6 px-6"
                  :style="{ color: theme.mediaText }"
                >
                  <Icon
                    :icon="
                      {
                        VIDEO: 'i-lucide-video',
                        DOCUMENT: 'i-lucide-file-text',
                      }[headerMediaPreview.format] || 'i-lucide-image'
                    "
                    class="size-7"
                  />
                  <p class="text-xs text-center">
                    {{ mediaPlaceholderLabel }}
                  </p>
                </div>
              </div>
            </div>

            <div class="p-2">
              <!-- Header text -->
              <p
                v-if="headerText"
                class="text-base font-semibold leading-snug"
                :style="{ color: theme.headerText }"
                v-html="headerHtml"
              />

              <!-- Body text -->
              <p
                v-if="bodyText"
                class="text-sm my-auto"
                :style="{ color: theme.text }"
                v-html="bodyHtml"
              />

              <!-- Footer text -->
              <p
                v-if="footerText"
                class="text-xs mt-2 leading-tight pr-16"
                :style="{ color: theme.footer }"
              >
                {{ footerText }}
              </p>

              <!-- Timestamp + delivery indicators -->
              <span
                class="absolute bottom-1 right-2 flex items-center gap-0.5 text-xs"
                :style="{ color: theme.time }"
              >
                {{ previewTime }}
                <!-- Double check delivery indicator -->
                <svg
                  width="16"
                  height="11"
                  viewBox="0 0 16 11"
                  fill="none"
                  class="ml-0.5"
                >
                  <path
                    d="M11.07 0.66L4.98 6.75L2.91 4.68L2.2 5.39L4.98 8.17L11.78 1.37L11.07 0.66Z"
                    :fill="theme.checkColor"
                  />
                  <path
                    d="M14.07 0.66L7.98 6.75L7.55 6.32L6.84 7.03L7.98 8.17L14.78 1.37L14.07 0.66Z"
                    :fill="theme.checkColor"
                  />
                </svg>
              </span>
            </div>
          </div>

          <!-- Button previews -->
          <div v-if="buttonPreviewItems.length">
            <button
              v-for="(button, index) in buttonPreviewItems"
              :key="`button-preview-${index}`"
              type="button"
              class="w-full flex items-center justify-center gap-2 px-4 py-3 text-sm transition-colors text-center font-medium"
              :style="{
                color: theme.buttonText,
                borderTop: `1px solid ${theme.buttonBorder}`,
              }"
            >
              <Icon
                v-if="button.icon"
                :icon="button.icon"
                class="size-4"
                :style="{ color: theme.buttonText }"
              />
              <div class="min-w-0 text-center">
                <span class="block truncate">{{ button.text }}</span>
              </div>
            </button>
          </div>
        </div>
      </div>

      <!-- Empty state -->
      <div
        v-else
        class="text-center flex flex-col items-center gap-2"
        :style="{ color: theme.emptyText }"
      >
        <Icon icon="i-lucide-message-square" class="size-10" />
        <p class="text-sm font-medium" :style="{ color: theme.emptyHeading }">
          {{ t('MESSAGE_TEMPLATES.BUILDER.PREVIEW.NO_CONTENT') }}
        </p>
      </div>
    </div>
  </div>
</template>
